import React, { useState } from "react";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useToast } from "../../hooks/use-toast";
import { FileBarChart, Loader2 } from "lucide-react";

const REPORTS = [
  { key: "audit_readiness", name: "Audit Readiness Report", description: "Per-framework readiness % and failing controls." },
  { key: "gap_analysis", name: "Gap Analysis", description: "Controls with no evidence or failing health, by framework." },
  { key: "soa", name: "Statement of Applicability", description: "Every control, its applicability and current status." },
  { key: "policy_gap", name: "Policy Gap Report", description: "Policies still in draft or overdue for review." },
];

export default function ComplianceReports() {
  const { toast } = useToast();
  const [generating, setGenerating] = useState(null);
  const [result, setResult] = useState(null);

  const generate = async (report) => {
    setGenerating(report.key);
    try {
      const [ctRes, fwRes, polRes] = await Promise.all([
        api.get("/compliance/controls"),
        api.get("/compliance/frameworks"),
        api.get("/compliance/policies"),
      ]);
      const controls = ctRes.data.controls || [];
      const frameworks = fwRes.data.frameworks || [];
      const policies = polRes.data.policies || [];
      const lines = [];

      if (report.key === "audit_readiness") {
        frameworks.filter((f) => f.enabled).forEach((f) => {
          const scoped = controls.filter((c) => c.frameworks?.includes(f.key));
          const healthy = scoped.filter((c) => c.health === "healthy").length;
          const pct = scoped.length ? Math.round((healthy / scoped.length) * 100) : 0;
          lines.push(`${f.name}: ${pct}% ready (${healthy}/${scoped.length} controls healthy)`);
        });
      } else if (report.key === "gap_analysis") {
        controls.filter((c) => c.health !== "healthy").forEach((c) => {
          lines.push(`${c.code} — ${c.title} [${c.health}] (${(c.frameworks || []).join(", ")})`);
        });
        if (lines.length === 0) lines.push("No gaps found — every control is healthy.");
      } else if (report.key === "soa") {
        controls.forEach((c) => lines.push(`${c.code} — ${c.title}: ${c.status} (${c.health})`));
      } else if (report.key === "policy_gap") {
        policies.filter((p) => p.status !== "published").forEach((p) => lines.push(`${p.code} — ${p.name}: ${p.status}, review due ${p.review_date || "n/a"}`));
        if (lines.length === 0) lines.push("No policy gaps — all policies published.");
      }

      setResult({ title: report.name, lines });
    } catch (e) {
      toast({ title: "Couldn't generate report", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setGenerating(null); }
  };

  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="compliance-reports-page">
      {REPORTS.map((r) => (
        <div key={r.key} className="rounded-xl border border-border bg-card p-4 flex items-start gap-3" data-testid={`compliance-report-card-${r.key}`}>
          <div className="w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
            <FileBarChart className="w-4.5 h-4.5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[13.5px] font-semibold">{r.name}</div>
            <div className="text-[11.5px] text-muted-foreground mt-0.5">{r.description}</div>
            <button
              type="button"
              onClick={() => generate(r)}
              disabled={generating === r.key}
              data-testid={`compliance-report-generate-${r.key}`}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-primary hover:underline mt-2 disabled:opacity-60"
            >
              {generating === r.key && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Generate
            </button>
          </div>
        </div>
      ))}

      <Dialog open={!!result} onOpenChange={(v) => { if (!v) setResult(null); }}>
        <DialogContent className="max-w-lg max-h-[70vh] overflow-y-auto" data-testid="compliance-report-result-modal">
          <DialogHeader><DialogTitle>{result?.title}</DialogTitle></DialogHeader>
          <ul className="space-y-1.5 text-[12.5px] py-1">
            {result?.lines.map((l, i) => <li key={i} className="rounded-md bg-muted/40 px-2.5 py-1.5">{l}</li>)}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}
